import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { GuestTag } from 'restaurantAdmin/restaurants/apiHelpers';
import type { VipTier } from 'restaurantAdmin/settings/general/vipProgramSection/apiHelpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getGuests } from './apiHelpers';

const GUEST_REQUEST_LIMIT = 50;

export const restaurantGuestsQueryKey = (restaurantId: string) =>
  ['restaurants', restaurantId, 'restaurantGuests'] as const;

export const guestBookGuestQueryKey = (restaurantId: string, guestId: string) =>
  ['restaurants', restaurantId, 'guestBookGuest', guestId] as const;

interface RestaurantGuestsRequestParams {
  search?: string;
}

export const useGuestBookFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateTagSearchParams = (selectedTags: GuestTag[]) => {
    const params = new URLSearchParams(searchParams);
    params.delete('tag');
    selectedTags.forEach((tag) => {
      params.append('tag', tag.title);
    });
    setSearchParams(params);
  };

  const updateVipTierSearchParams = (selectedTiers: VipTier[]) => {
    const params = new URLSearchParams(searchParams);
    params.delete('vipTierId');
    selectedTiers.forEach((tier) => {
      params.append('vipTierId', tier.id);
    });
    setSearchParams(params);
  };

  const updateVisitDateSearchParams = ({
    startDate,
    endDate,
  }: {
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    if (startDate) {
      params.set('startDate', startDate);
    } else {
      params.delete('startDate');
    }
    if (endDate) {
      params.set('endDate', endDate);
    } else {
      params.delete('endDate');
    }
    setSearchParams(params);
  };

  const updateMarketingOptInSearchParam = (marketingOptedInOnly: boolean) => {
    const params = new URLSearchParams(searchParams);

    if (marketingOptedInOnly) {
      params.set('marketingOptedInOnly', 'true');
    } else {
      params.delete('marketingOptedInOnly');
    }

    setSearchParams(params);
  };

  const hasActiveFilters =
    searchParams.has('tag') ||
    searchParams.has('vipTierId') ||
    searchParams.has('startDate') ||
    searchParams.has('endDate') ||
    searchParams.get('marketingOptedInOnly') === 'true';

  return {
    filterSearchParams: searchParams,
    hasActiveFilters,
    updateMarketingOptInSearchParam,
    updateVisitDateSearchParams,
    updateTags: updateTagSearchParams,
    updateVipTiers: updateVipTierSearchParams,
  };
};

export const useRestaurantGuests = (params: RestaurantGuestsRequestParams) => {
  const { id: restaurantId } = useRestaurant();
  const { filterSearchParams } = useGuestBookFilterParams();

  const { search = '' } = params;

  return useInfiniteQuery({
    queryKey: [
      ...restaurantGuestsQueryKey(restaurantId),
      {
        search,
        filters: filterSearchParams.toString(),
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const guests = await getGuests({
        restaurantId,
        limit: GUEST_REQUEST_LIMIT,
        offset: pageParam,
        search,
        filters: filterSearchParams,
      });
      return guests;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length < GUEST_REQUEST_LIMIT) {
        return undefined;
      }
      return pages.length * GUEST_REQUEST_LIMIT;
    },
  });
};
